import React, { Fragment, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import AccessLevelService, { siteModerator } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import GiftsStore from 'stores/GiftsStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import BalanceInfoMobile from 'components/BalanceInfoMobile/BalanceInfoMobile';
import Button from 'components/Button/Button';
import ContentCard from 'components/ContentCard/ContentCard';
import { GIFT_GOLD_STAR_NOTIFICATION_EXTRA } from 'enum/helpers';

import SendFeedback from './SendFeedback/SendFeedback';
import UserGift from './UserGift/UserGift';
import styles from './SendUserGiftModal.css';


const SendUserGift = ({ close, gift, isDesktop, sendGoldGift }) => {
  const { profileName } = ProfileStore;
  const { giftSendPending, giftSendSuccessful, giftSendError } = GiftsStore;
  const { showBuyTokensModal } = TippingStore;
  const { user: { accessLevel } } = UserStore;
  const { balance } = PaymentStore;
  const hasModeratorAccess = AccessLevelService.isMin(siteModerator, accessLevel);
  const enoughBalance = balance >= gift.price;
  const isGoldGift = gift.id === GIFT_GOLD_STAR_NOTIFICATION_EXTRA;
  const [anonymous, setAnonymous] = useState(false);

  const handleSendGift = useCallback(() => {
    const payload = {
      recipientName: profileName,
      giftId: gift.id,
      message: '',
      anonymous,
    };
    if (isGoldGift) {
      if (isDesktop) {
        ModalService.showModal(ModalService.MODAL_KEYS.SEND_QUICK_GIFT_MODAL, {
          showClose: false,
          isGoldSelected: true,
        });
      } else {
        sendGoldGift();
      }
    } else {
      GiftsStore.sendGift(payload);
    }
  }, [
    anonymous,
    gift.id,
    isDesktop,
    isGoldGift,
    profileName,
    sendGoldGift,
  ]);

  const onOpenBuyTokensModal = useCallback(() => {
    close();
    showBuyTokensModal();
  }, [close, showBuyTokensModal]);

  const toggleCheckbox = useCallback(() => {
    setAnonymous(!anonymous);
  }, [anonymous]);

  const balanceInfoElement = useMemo(() => {
    if (isDesktop) {
      return null;
    }

    return (
      <BalanceInfoMobile
        areTokensNeeded={!enoughBalance}
        onClick={onOpenBuyTokensModal}
      />
    );
  }, [
    enoughBalance,
    isDesktop,
    onOpenBuyTokensModal,
  ]);

  return (
    <Fragment>
      <ContentCard
        className={styles.noMargin}
        contentClass={styles.SendUserGiftModal}
        footer={
          isDesktop && (
            <div className={styles.footerActions}>
              <Button
                className={styles.footerCancelBtn}
                colour="transparent"
                label={translate('v2profile.gifts.cancel')}
                onClick={close}
              />
            </div>
          )
        }
        footerClass={styles.footerSendGiftModal}
      >
        {giftSendSuccessful || giftSendError ? (
          <SendFeedback perfomerName={profileName} />
        ) : (
          <UserGift
            anonymous={anonymous}
            close={close}
            gift={gift}
            giftSendPending={giftSendPending}
            handleSendGift={handleSendGift}
            hasBalance={enoughBalance}
            hasModeratorAccess={hasModeratorAccess}
            isDesktop={isDesktop}
            toggleCheckbox={toggleCheckbox}
          />
        )}
      </ContentCard>
      {balanceInfoElement}
    </Fragment>
  );
};

SendUserGift.propTypes = {
  close: PropTypes.func.isRequired,
  gift: PropTypes.object.isRequired,
  isDesktop: PropTypes.bool,
  sendGoldGift: PropTypes.func,
};

SendUserGift.defaultProps = {
  isDesktop: false,
  sendGoldGift: () => undefined,
};

export default observer(SendUserGift);
